import React, { useState, useEffect } from "react";
import { useResetPasswordMutation } from "../slices/userApiSlice";
import { toast } from "sonner";
import "./css/Newpassword.css";
import { FaLock } from "react-icons/fa";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import Header from "../components/LandingComponents/Header";
import LandingFooter from "../components/LandingComponents/LandingFooter";
import { useLocation, useNavigate, Link } from "react-router-dom";

const Newpasswordpage = () => {
  const { state } = useLocation();
  const email = state?.email;
  const otp = localStorage.getItem("otp");

  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const navigate = useNavigate();

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return "Weak";
    if (strength === 3 || strength === 4) return "Medium";
    if (strength === 5) return "Strong";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPasswordError(""); // Clear previous error

    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^])[A-Za-z\d@$!%*?&#^]{8,}$/;

    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    if (!strongPasswordRegex.test(password)) {
      setPasswordError(
        "Password must be 8+ characters and include uppercase, lowercase, number, and special character",
      );
      return;
    }

    const toastId = toast.loading(
      "Processing: Please wait while we reset your password.",
    );

    try {
      const res = await resetPassword({
        email,
        otp,
        newPassword: password,
      }).unwrap();

      toast.dismiss(toastId);
      toast.success(
        res.message ||
          "Password Reset Successful: You can now log in with your new password!",
      );

      setPassword("");
      setConfirmPassword("");
      navigate("/login");
    } catch (error) {
      toast.dismiss(toastId);
      toast.error(
        error?.data?.message || "Error: Failed to reset password. Try again!",
      );
    }
  };

  useEffect(() => {
    if (!email) {
      toast.error(
        "Email not found: No email provided. Please check the email link or try again.",
      );
    }

    if (!otp) {
      toast.error("OTP not found: Please try again or request a new OTP.");
      navigate("/otp");
    }
  }, [email, otp, navigate]);

  return (
    <div className="login-wrapper">
      <Header />
      <div className="login-main">
        <div className="login-bg-overlay"></div>

        <div className="login-card">
          <div className="login-card-header">
            <h2 className="login-title">New Password</h2>
            <p className="login-subtitle">
              Please create a strong password to secure your account.
            </p>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-field">
              <label className="login-label">New Password</label>
              <div className="login-input-wrapper">
                <FaLock className="login-field-icon" />
                <input
                  required
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  className="login-input"
                  value={password}
                  onChange={(e) => {
                    const newPassword = e.target.value;
                    setPassword(newPassword);
                    setPasswordStrength(getPasswordStrength(newPassword));
                    setPasswordError("");
                  }}
                />
                <button
                  type="button"
                  className="login-toggle-btn"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <AiFillEye /> : <AiFillEyeInvisible />}
                </button>
              </div>
            </div>

            {/* Password Strength Indicator */}
            {password && (
              <div className={`strength-meter strength-${passwordStrength}`}>
                <div className="strength-dot"></div>
                <span>Strength: {passwordStrength}</span>
              </div>
            )}

            <div className="login-field">
              <label className="login-label">Confirm Password</label>
              <div className="login-input-wrapper">
                <FaLock className="login-field-icon" />
                <input
                  required
                  type={showPassword ? "text" : "password"}
                  placeholder="Repeat new password"
                  className="login-input"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordError("");
                  }}
                />
              </div>
              {passwordError && (
                <span className="login-error-text">{passwordError}</span>
              )}
            </div>

            <button
              type="submit"
              className="login-btn-primary"
              disabled={isLoading}
              style={{
                backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                color: "white",
              }}
            >
              {isLoading ? "Processing..." : "Update Password"}
            </button>
          </form>

          <div className="login-card-footer">
            <Link to="/login" className="login-back-link">
              <span>Return to Login</span>
            </Link>
          </div>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
};

export default Newpasswordpage;
